import { useEffect, useRef } from 'react';
import { audioEngine } from '@/audio/engine/AudioEngine';
import './LevelWave.css';

interface LevelWaveProps {
  bars?: number;
  color?: string;
  height?: number;
  /** Freeze the display — used when the session is paused. */
  paused?: boolean;
}

/**
 * The live input meter.
 *
 * Written straight to the DOM from a `requestAnimationFrame` loop, never
 * through React state: this updates 60 times a second and has no business
 * touching the reconciler while a session is running.
 */
export function LevelWave({ bars = 28, color = 'var(--success)', height = 28, paused = false }: LevelWaveProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const historyRef = useRef<number[]>(Array.from({ length: bars }, () => 0.04));

  useEffect(() => {
    if (paused) return;
    let raf = 0;
    const tick = () => {
      const container = containerRef.current;
      if (container) {
        const level = audioEngine.getSnapshot().level;
        const history = historyRef.current;
        history.push(Math.max(0.04, Math.min(1, level)));
        if (history.length > bars) history.shift();

        const children = container.children;
        for (let i = 0; i < children.length; i++) {
          const bar = children[i] as HTMLElement;
          bar.style.transform = `scaleY(${history[i] ?? 0.04})`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [bars, paused]);

  return (
    <div
      className="level-wave"
      ref={containerRef}
      style={{ height, color }}
      aria-hidden="true"
    >
      {Array.from({ length: bars }, (_, index) => (
        <span key={index} className="level-wave__bar" />
      ))}
    </div>
  );
}
